import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { FaSpinner, FaPlus, FaPaperclip } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { ColoredDeliveryIcon, CloseIcon, PlusIcon, InfoIcon } from 'ui-component/custom-icons/icons';
import Select from 'react-select';
import InputGroup from 'react-bootstrap/InputGroup';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import useIsMobile from 'utils/isMobileUtils';
import { useDropzone } from 'react-dropzone';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';


// ✅ Clear
const PicklistCreateShipmentModal = ({ openPopup, details, handleClose, handleReloadData }) => {

    const [disabledCreateShipmentButton, setDisabledCreateShipmentButton] = useState(false);
    const [shippingCompanies, setShippingCompanies] = useState([]);
    const [shipmentReferenceNo, setShipmentReferenceNo] = useState([]);
    const [defaultWeightUnit, setDefaultWeightUnit] = useState('');

    const [currencyList, setCurrencyList] = useState([]);
    const [attachmentCollections, setAttachmentCollections] = useState([]);
    const [optimizedSerials, setOptimizedSerials] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([]);
    const [inProgressFileUploadCollection, setInProgressFileUploadCollection] = useState([]);
    const [removeFilenameFromInProgressFileUploadCollection, setRemoveFilenameFromInProgressFileUploadCollection] = useState(null);

    const isMobile = useIsMobile();

    const [shipmentDetails, setShipmentDetails] = useState({
        picklist_id: details?.picklist_id,
        shipping_company_id: '',
        tracking_number: '',
        weight: '',
        weight_unit: 'kg',
        shipping_currency_id: '',
        shipping_cost: '',
        additional_charges: [],
    });

    const weightUnits = [
        { label: 'Kilogram', value: 'kg' },
        { label: 'Gram', value: 'g' },
    ];

    const formData = new FormData();

    useEffect(() => {

        
        const requests = details?.items?.map((item) => {
            return {
                id: item.id,
                picked_quantity: item.picked_quantity,
                quantity: item.quantity,
                show_scanner: item.show_scanner,
            }
        }).filter((item) => item.picked_quantity > 0);


        api.getSelectOptionsList('currency').then((res) => {
            const collection = res?.data?.map((item) => ({ id: item.id, label: item.currency, value: item.id }));
            setCurrencyList(collection);
            const defaultCurrency = res?.data?.find((item) => item.currency === 'USD ($)');
            setShipmentDetails({
                ...shipmentDetails,
                shipping_currency_id: defaultCurrency?.id
            })
		});

        api.getNextReferenceNumber('shipment').then((res) => {
            if (res?.status) {
                setShipmentReferenceNo(res?.data?.next_reference_no);
            }
        });
		api.getSelectOptionsList('shipping_company').then((res) => {
			// option_product_name: "11P256MIXAA+ - iPhone 11 Pro 256GB Mixed AA+ - 0 stock"
			const data = res.data.map((item) => ({
				...item,
				label: `${item.name}`,
				value: item.id,
			}));
			setShippingCompanies(data);
        });
        setDefaultWeightUnit('Kilogram');
        setShipmentDetails({
            ...shipmentDetails,
            picklist_id: details?.picklist_id,
            weight_unit: 'kg',
        })
        setOptimizedSerials(requests);
    }, []);

    const disablepopup = () => {
        handleClose();
    };

    const handleChangeWeight = (e) => { 
        
        const weightValue = parseFloat(e.target.value);

        if (!isNaN(weightValue)) {
            setShipmentDetails({
                ...shipmentDetails,
                weight: weightValue
            })
        } else {
            setShipmentDetails({
                ...shipmentDetails,
                weight: 0
            })
        }
    }


	const onDrop = useCallback((acceptedFiles) => {
        toast.error('Invalid file extension. Please select a JPEG, JPG, PNG, or PDF file.');
	}, []);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: '.pdf, .jpeg, .jpg, .png',
		multiple: false
    });
    


    const allowedExtensions = ['pdf', 'jpeg', 'jpg', 'png'];
    const validateFileExtension = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return allowedExtensions.includes(extension);
    };


	const onAttachmentDrop = useCallback((acceptedFiles) => {

        formData.append('picklist_id', shipmentDetails?.picklist_id);
        const attachments = acceptedFiles.map((file, index) => {

            let sizeStr = "";
            const sizeInKB = file.size / 1024;
            if (sizeInKB >= 1024) {
                const sizeInMB = sizeInKB / 1024;
                sizeStr = `${sizeInMB.toFixed(2)} MB`;
            } else {
                sizeStr = `${sizeInKB.toFixed(2)} KB`;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size should not exceed more than 5MB.');
                // Remove all attachments from formData
                if (typeof formData.delete === 'function') {
                    // Remove all 'attachments[]' entries
                    const keysToDelete = [];
                    for (let pair of formData.entries()) {
                        if (pair[0] === 'attachments[]') {
                            keysToDelete.push(pair[0]);
                        }
                    }
                    keysToDelete.forEach(key => formData.delete(key));
                }
                return;
            }

            if (validateFileExtension(file?.name)) {
                // uploadAttachment(file);

                formData.append('attachments[]', file);
                let values = {
                    filename: file.name,
                    size: sizeStr,
                    extension: file.type,
                }
                setInProgressFileUploadCollection((oldData) => [...oldData, values]);
                return {
                    id: '',
                    filename: file?.name,
                    size: sizeStr,
                    attachment: file,
                    allowed: true
                }

            } else {
                return {
                    id: '',
                    filename: file?.name,
                    size: sizeStr,
                    attachment: file,
                    allowed: false
                }
            }
        })

        try {
            toast.promise(
                api
                    .uploadShipmentAttachment(formData)
                    .then(
                        (res) => {
                            if (res.status) {
                                const datas = res.data.map(item => {

                                    setInProgressFileUploadCollection((oldData) =>
                                        oldData.filter((item) => item.filename !== item?.filename)	
                                    );
                                    return {
                                        id: item.id,
                                        filename: item.filename,
                                        file_size: item.file_size,
                                        extension: item.extension,
                                    }
                                })
                                setAttachmentCollections([...attachmentCollections, ...datas]);
                                formData.append('attachments', null);
                            }

                            return res?.status ? res : null;
                        },
                        () => {}
                    ),
                {
                    loading: 'uploading file...',
                    success: (data) => `${data.message}`,
                    error: <b>Failed to upload file</b>
                }
            );
            
        } catch (error) {
            toast.error('Error uploading file');
        }

        return false;
	}, []);
	const { getRootProps: getAttachmentRootProps, getInputProps: getAttachmentInputProps } = useDropzone({ onDrop: onAttachmentDrop });




    const submitShipmentForm = () => {
        let requests = shipmentDetails;
        requests.items = optimizedSerials;
        requests.attachment_ids = attachmentCollections
        requests.additional_charges = additionalCharges;

        if (!requests?.picklist_id) {
            toast.error('Picklist is required');
            return;
        } else if (!requests?.shipping_company_id) {
            toast.error('Shipping Company is required');
            return;
        } else if (!requests?.tracking_number) {
            toast.error('Airway bill is required');
            return;
        } else if (!requests?.shipping_currency_id) {
            toast.error('Currency is required');
            return;
        } else if (!requests.shipping_cost.toString().trim()) {
            toast.error('Shipping cost is required');
            return;
        } else if (!requests.weight.toString().trim()) {
            toast.error('Weight is required');
            return;
        }
        setDisabledCreateShipmentButton(true);
        toast.promise(
            api
            .createShipmentFromPicklist(shipmentDetails?.picklist_id, requests)
            .then(
                (res) => {
                        if (res.status) {
                            handleReloadData()
                        }
    
                        return res?.status ? res : null;
                    },
                    () => { }
                ),
            {
                loading: 'Creating shipment...',
                success: (data) => `${data.message}`,
                error: <b>Failed to add shipment</b>
            }
        );
    }



    return (
        <Modal show={openPopup} onHide={disablepopup} centered size="md">
            <Modal.Body style={{ padding: '0' }}>
                
                <div className="p-2 swipe-rtl-animation">
                    <div>
                        <div className="mb-1 ml-0 pl-0">
                            <button
                                className="btn modal-close-icon"
                                onClick={handleClose}
                                style={{
                                    zIndex: 1,
                                }}
                            >
                                <CloseIcon height={'16px'} width={'16px'} />
                            </button>
                        </div>
                        <div style={{
                            maxHeight: '550px',
                            width: '100%',
                            overflowY: 'auto',
                            padding: '20px 25px 0px 25px'
                        }}>
                            <div className="row">
                                <div className="col-12 text-center">
                                    <ColoredDeliveryIcon height='60px' width='60px' />
                                </div>
                            </div>
                            <div className="row align-items-top">
                                <div className="col-12 text-center " style={{ padding: '10px 0 20px 0' }}>
                                    <div style={{ fontSize: '24px' }}>
                                        Create Shipment
                                    </div>
                                    <div className='textGray'>
                                        Manage shipment details for accurate <br />tracking and smooth delivery.
                                    </div>
                                    
                                    
                                </div>
                            </div>
                            <div className="row align-items-top">
                                <div className="col-12 text-left " style={{ padding: '5px 0' }}>
                                    <label>Reference No.</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={shipmentReferenceNo}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                            <div className="row align-items-top">
                                <div className="col-12 col-md-6 text-left " style={{ padding: '5px 0' }}>
                                    <label>Shipping Provider</label>
                                    <Select
                                        options={shippingCompanies}
                                        className="rs-container"
                                        classNamePrefix="rs"
                                        placeholder="Select or type..."
                                        isMulti={false}
                                        onChange={(e) => {
                                            setShipmentDetails({
                                                ...shipmentDetails,
                                                shipping_company_id: e.value
                                            })
                                        }}
                                    />
                                </div>
                                <div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 5px 5px' }}>
                                    <label>Airway bill code</label>
                                    
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="inputName"
                                        value={shipmentDetails?.tracking_number}
                                        placeholder='Enter airway bill code'
                                        onChange={(e) => {
                                            setShipmentDetails({
                                                ...shipmentDetails,
                                                tracking_number: e.target.value
                                            })
                                        }}
                                        style={{
                                            minHeight: '35px',
                                        }}
                                    />
                                </div>
                            </div>
                            
                            <div className="row align-items-top">
                                <div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 0 0' }}>
                                    <label>Shipping cost</label>
                                    <div className="row">

                                        <div className="col-12 d-flex input-group-2-form input-group-2-form-currency">
                                            <Select
                                                options={currencyList}
                                                className="rs-container left-input"
                                                classNamePrefix="rs"
                                                placeholder="Select"
                                                isMulti={false}
                                                onChange={(e) => { 
                                                    setShipmentDetails({
                                                        ...shipmentDetails,
                                                        shipping_currency_id: e.value
                                                    })
                                                }}
                                                value={currencyList?.find(option => option.id === shipmentDetails?.shipping_currency_id) || null}
                                            />
                                            <input
                                                type="number"
                                                className="form-control h36 right-input ml6"
                                                id="inputName"
                                                value={shipmentDetails?.shipping_cost}
                                                placeholder='0.00'
                                                onChange={(e) => {
                                                    setShipmentDetails({
                                                        ...shipmentDetails,
                                                        shipping_cost: e.target.value
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>

                                </div>
                                <div className="col-12 col-md-6 text-left " style={{ padding: '5px 0 5px 5px' }}>
                                    <label>Weight</label>
                                    <div className="custom-input-group">

                                        <InputGroup className="d-flex">
                                            <DropdownButton
                                                variant="outline-secondary"
                                                title={defaultWeightUnit}
                                                id="input-group-dropdown-1"
                                            >
                                                {
                                                    weightUnits.map((unit) => { 
                                                        return (
                                                            <Dropdown.Item
                                                                href="#"
                                                                key={unit.value}
                                                                onClick={() => { 
                                                                    setShipmentDetails({
                                                                        ...shipmentDetails,
                                                                        weight_unit: unit.value
                                                                    })
                                                                    setDefaultWeightUnit(unit.label);
                                                                }}
                                                            >
                                                                {unit.label}
                                                            </Dropdown.Item>
                                                        )
                                                    })
                                                }
                                            </DropdownButton>
                                            <input
                                                type="number"
                                                id="input-example"
                                                name="input-name"
                                                className="form-control"
                                                placeholder="0"
                                                onChange={(e) => {
                                                    handleChangeWeight(e)
                                                }}
                                                value={shipmentDetails?.weight}
                                                autoComplete={'off'}
                                                autoFocus
                                            />
                                        </InputGroup>
                                    </div>

                                </div>
                            </div>


                            <div className="row align-items-top">
                                <div className="col-6 text-left " style={{ padding: '10px 0 0 0' }}>
                                    <label style={{ marginTop: '7px' }}>Charges</label>
                                </div>
                                <div className="col-6 text-right d-flex justify-content-end" style={{ padding: '10px 0 0 0' }}>

                                    <button
                                        className="btn btn-main-primary btn-outlined-main-primary"
                                        style={{  minWidth: '0px' }}
                                        onClick={() => {
                                            setAdditionalCharges([
                                                ...additionalCharges,
                                                {
                                                    currency_id: shipmentDetails?.shipping_currency_id,
                                                    amount: '',
                                                    description: ''
                                                }
                                            ]);
                                        }}
                                    >
                                        <PlusIcon height={'16px'} width={'16px'} />
                                    </button>
                                </div>
                            </div>
                            {
                                additionalCharges?.length > 0 && (
                                    additionalCharges?.map((charge, index) => (
                                        <>
                                            <hr />
                                            <div className="row align-items-top" key={index}>
                                                <div className="col-11 text-left " style={{ padding: '0' }}>
                                                    
                                                    <div className="row">
                                                        <div className="col-12 col-md-6">
                                                            <div className="row mb-2">
                                                                <div className="col-12">
                                                                    <label>Amount</label>
                                                                </div>
                                                                <div className="col-12 d-flex input-group-2-form input-group-2-form-currency">
                                                                    
                                                                    <Select
                                                                        options={currencyList}
                                                                        className="rs-container left-input"
                                                                        classNamePrefix="rs"
                                                                        placeholder="Select"
                                                                        isMulti={false}
                                                                        value={currencyList?.find(option => option.id === charge?.currency_id) || null}
                                                                        onChange={(e) => {
                                                                            const updatedCharges = [...additionalCharges];
                                                                            updatedCharges[index].currency_id = e.value;
                                                                            setAdditionalCharges(updatedCharges);
                                                                        }}
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        className="form-control h36 right-input ml6"
                                                                        id="inputName"
                                                                        value={charge?.amount}
                                                                        placeholder='0.00'
                                                                        onChange={(e) => {
                                                                            const updatedCharges = [...additionalCharges];
                                                                            updatedCharges[index].amount = e.target.value;
                                                                            setAdditionalCharges(updatedCharges);
                                                                        }}
                                                                    
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="row">
                                                                <div className="col-12">
                                                                    <label>Description</label>
                                                                </div>
                                                                <div className="col-12 d-flex input-group-2-form input-group-2-form-currency">
                                                                    
                                                                    <textarea
                                                                        onChange={(e) => {
                                                                            const updatedCharges = [...additionalCharges];
                                                                            updatedCharges[index].description = e.target.value;
                                                                            setAdditionalCharges(updatedCharges);
                                                                        }}
                                                                        value={charge?.description}
                                                                        className="form-control textArea"
                                                                        placeholder="Enter description"
                                                                        style={{
                                                                            height: '35px'
                                                                        }}
                                                                    ></textarea>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                                <div className="col-1 d-flex align-items-center">
                                                        <button
                                                            className="btn modal-close-icon"
                                                            onClick={() => {
                                                                const updatedCharges = additionalCharges.filter((_, i) => i !== index);
                                                                setAdditionalCharges(updatedCharges);
                                                            }}
                                                            style={{
                                                                position: 'relative',
                                                                marginTop: '-3px',
                                                                marginLeft: '7px'
                                                            }}
                                                        >
                                                            <CloseIcon height={'10px'} width={'10px'} />
                                                        </button>
                                                </div>
                                                
                                            </div>
                                        </>
                                    ))
                                )
                            }

                            <div className="row align-items-top">
                                <div className="col-12 text-left " style={{ padding: '10px 0' }}>
                                
                                    <div {...getAttachmentRootProps()}>
                                        
                                        {/* <div
                                            className="add-item-empty-div"
                                        >
                                            <button type="button" className="btn btn-primary btn-outlined-main-primary dragText" >
                                                <FaPlus />
                                                Add file here
                                            </button>
                                        </div> */}

                                        <div className="add-item-empty-div" style={{ alignItems: 'center' }}>
                                            <div
                                                className="text-primary"
                                                style={{
                                                    marginTop: '1px',
                                                    padding: '5px 7px',
                                                    border: '2px solid #9e9e9e',
                                                    borderRadius: '32px',
                                                }}
                                            >
                                                <PlusIcon height="16px" width="16px" />
                                            </div>
                                            <div style={{ marginLeft: '10px' }}>
                                                <div
                                                    className='flex'
                                                    style={{
                                                        gap: '5px'
                                                    }}
                                                >
                                                    <span className="fw-500">Drag & Drop or Click to Upload</span>
                                                    <span className="text-gray-700">OR</span>
                                                    <span className="text-blue-600 fw-500">Browse Files</span>
                                                </div>
                                                <div className="text-gray-600 fw-400 mt-1 flex align-items-center" style={{ fontSize: '10px', gap: '5px' }}>
                                                    <InfoIcon width='14px' height='14px' />
                                                    <span>
                                                        png, jpg, jpeg or pdf (Max file size: 5MB)
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <input {...getAttachmentInputProps({ disabled: false })} style={{ display: 'none' }} />{' '}
                                    </div>
                                </div>
                            </div>
                            {
                                inProgressFileUploadCollection?.length > 0 && (
                                    [...inProgressFileUploadCollection].reverse().map((attachment, index) => (
                                        <div className="row align-items-top" key={index} style={{ padding: '0' }}>
                                            <div className="col-12" style={{ padding: '0' }}>
                                                {/* <div className={`card ${collapsedAttachments[index] ? 'shadow-sm' : '' } bg-white mb-2 w-100 border card-border-gray`}> */}
                                                <div className={`card bg-white mb-2 w-100 border card-border-gray relative`}>
                                                    {/* <div className={`${collapsedAttachments[index] ? 'card-header-radius-0' : 'card-header-radius-360'}`} style={{ padding: '0px 10px' }}> */}
                                                    <div className={`card-header-radius-0`} style={{ padding: '0px 15px' }}>
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <div className="section-head row" style={{ padding: '0px 10px !important', paddingLeft: '0px' }}>
                                                                    <div style={{ alignItems: 'center', fontSize: '14px' }} className="col-10">
                                                                        <div className="flex align-items-center" style={{ gap: '10px' }}>
                                                                            <div>
                                                                                {attachment?.extension === 'application/pdf' ? (
                                                                                    <PictureAsPdfOutlinedIcon style={{ fontSize: 24 }} />
                                                                                ) : (
                                                                                    <InsertPhotoOutlinedIcon style={{ fontSize: 24 }} />
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <div className="">
                                                                                    {
                                                                                        isMobile ? (
                                                                                            'Attachment'
                                                                                        ) : (
                                                                                            attachment?.filename
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                                <div className="text-gray-600" style={{ fontSize: '12px' }}>{attachment?.size ?? 'Size not mentioned'}</div>

                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="actions col-2 flex align-items-center justify-end" style={{ gap: '8px' }}>
                                                                        <div 
                                                                            className="payment-modal-attachment-delete"
                                                                           
                                                                        >
                                                                            <svg id="Component_127_1" height="12" width="12" data-name="Component 127 – 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.295 19.289">
                                                                                <path id="Icon_ionic-ios-close" data-name="Icon ionic-ios-close" d="M23.219,20.933l6.891-6.891a1.615,1.615,0,0,0-2.284-2.284L20.935,18.65l-6.891-6.891a1.615,1.615,0,1,0-2.284,2.284l6.891,6.891L11.76,27.825a1.615,1.615,0,1,0,2.284,2.284l6.891-6.891,6.891,6.891a1.615,1.615,0,1,0,2.284-2.284Z" transform="translate(-11.285 -11.289)" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="card-footer p-0 border-0 bg-transparent" style={{ height: '4px', marginTop: '-4px' }}>
                                                        <div className="progress" style={{ height: '4px', borderRadius: '0', backgroundColor: 'transparent' }}>
                                                            <div 
                                                                className="progress-bar progress-bar-animated" 
                                                                role="progressbar" 
                                                                style={{ 
                                                                    width: '100%', 
                                                                    background: 'linear-gradient(90deg, #007bff 0%, #00d4ff 50%, #007bff 100%)',
                                                                    backgroundSize: '200% 100%',
                                                                    animation: 'loading-slide 2s infinite linear'
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>


                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )
                            }
                            {
                                attachmentCollections?.length > 0 && (
                                    <div className="row m-0">
                                        <div className="col-12 text-left " style={{ padding: '10px 0' }}>
                                            {
                                                [...attachmentCollections]
                                                    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
                                                    .map((attachment, index) => (
                                                        // <div className="payment-modal-attachment-container" key={attachment.id || index}>
                                                        //     <div className="row">
                                                        //         <div className="col-12">
                                                        //             <div className="row">
                                                        //                 <div className="col-12" style={{ display: 'flex', alignItems: 'center' }}>
                                                        //                     <div className="payment-modal-attachment-icon" style={{ padding: '5px 0px', border: '0'}}>
                                                        //                         <FaPaperclip />
                                                        //                     </div>
                                                        //                     <div className="payment-modal-attachment-name">
                                                        //                         <div>{attachment?.name}</div>
                                                        //                     </div>
                                                        //                     <div className="payment-modal-attachment-delete">
                                                        //                         <svg id="Component_127_1" height="12" width="12" data-name="Component 127 – 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.295 19.289">
                                                        //                             <path id="Icon_ionic-ios-close" data-name="Icon ionic-ios-close" d="M23.219,20.933l6.891-6.891a1.615,1.615,0,0,0-2.284-2.284L20.935,18.65l-6.891-6.891a1.615,1.615,0,1,0-2.284,2.284l6.891,6.891L11.76,27.825a1.615,1.615,0,1,0,2.284,2.284l6.891-6.891,6.891,6.891a1.615,1.615,0,1,0,2.284-2.284Z" transform="translate(-11.285 -11.289)" />
                                                        //                         </svg>
                                                        //                     </div>
                                                        //                 </div>
                                                        //             </div>
                                                        //         </div>
                                                        //     </div>
                                                        // </div>
                                                        <div className="row align-items-top" key={index} style={{ padding: '0' }}>
                                                            <div className="col-12" style={{ padding: '0' }}>
                                                                <div className={`bg-white mb-2 w-100 border card-border-gray relative`} style={{ borderRadius: '10px' }}>
                                                                    <div className={`card-header-radius-360`} style={{ padding: '0px 15px', background: '#fff', borderBottom: 0 }}>
                                                                        <div className="row">
                                                                            <div className="col-12">
                                                                                <div className="section-head row" style={{ padding: '0px 10px !important', paddingLeft: '0px' }}>
                                                                                    <div style={{ alignItems: 'center', fontSize: '14px' }} className="col-10">
                                                                                        <div className="flex align-items-center" style={{ gap: '10px' }}>
                                                                                            <div>
																						        {attachment?.extension == 'pdf' ? (
                                                                                                    <PictureAsPdfOutlinedIcon style={{ fontSize: 24 }} />
                                                                                                ) : (
                                                                                                    <InsertPhotoOutlinedIcon style={{ fontSize: 24 }} />
                                                                                                )}
                                                                                            </div>
                                                                                            <div>
                                                                                                <div className="">
                                                                                                    {
                                                                                                        isMobile ? (
                                                                                                            'Attachment'
                                                                                                        ) : (
                                                                                                            attachment?.filename
                                                                                                        )
                                                                                                    }
                                                                                                </div>
                                                                                                
                                                                                                <div className="text-gray-600" style={{ fontSize: '12px' }}>
                                                                                                    {attachment?.file_size ? (
                                                                                                        attachment?.file_size
                                                                                                    ) : (
                                                                                                        'File size not available'
                                                                                                    )}
                                                                                                </div>

                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="actions col-2 flex align-items-center justify-end" style={{ gap: '8px' }}>
                                                                                        <div 
                                                                                            className="payment-modal-attachment-delete"
                                                                                            onClick={() => {
                                                                                                handleRemoveAttachment()
                                                                                            }}
                                                                                        >
                                                                                            <svg id="Component_127_1" height="12" width="12" data-name="Component 127 – 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.295 19.289">
                                                                                                <path id="Icon_ionic-ios-close" data-name="Icon ionic-ios-close" d="M23.219,20.933l6.891-6.891a1.615,1.615,0,0,0-2.284-2.284L20.935,18.65l-6.891-6.891a1.615,1.615,0,1,0-2.284,2.284l6.891,6.891L11.76,27.825a1.615,1.615,0,1,0,2.284,2.284l6.891-6.891,6.891,6.891a1.615,1.615,0,1,0,2.284-2.284Z" transform="translate(-11.285 -11.289)" />
                                                                                            </svg>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                            }
                                        </div>
                                    </div>
                                )
                            }

                        </div>
                            

                        <div className="row align-items-top">
                            <div className="col-12 text-left " style={{ padding: '20px 25px 10px' }}>
                                <button
                                    className="btn btn-main-primary btn-outlined-main-primary btn-block"
                                    style={{ marginRight: '12px' }}
                                    disabled={disabledCreateShipmentButton}
                                    onClick={() => {
                                        if (disabledCreateShipmentButton) {
                                            toast.error('Please wait, processing shipment creation...');
                                            return;
                                        } else {
                                            submitShipmentForm()
                                        }
                                    }}
                                >
                                {
                                    disabledCreateShipmentButton ? (
                                        <>
                                            <FaSpinner className="fa-spin" /> Creating shipment. Please wait...
                                        </>
                                    ) : (
                                        'Create Shipment'
                                    )
                                }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PicklistCreateShipmentModal;
